import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import React, { useMemo } from 'react'

const baseStyle = {
  borderRadius: '4px',
  cursor: 'pointer',
  minWidth: '0',
  flex: '1 1 0',
} as const

interface SelectableCardProps {
  isSelected: boolean
  onClick: () => void
  ariaLabel: string
  icon?: React.ReactNode
  title: React.ReactNode
  description: React.ReactNode
  compact?: boolean
}

const SelectableCard: React.FC<SelectableCardProps> = ({
  isSelected,
  onClick,
  ariaLabel,
  icon,
  title,
  description,
  compact = false,
}) => {
  const [css] = useStyletron()

  const { normalStyle, activeStyle } = useMemo(() => {
    const padding = compact ? '12px 16px' : '16px'
    return {
      normalStyle: css({
        ...baseStyle,
        padding,
        border: '1px solid #E0E0E0',
        transition: 'border-color 0.15s ease',
        ':hover': {
          borderColor: '#90CAF9',
        },
      }),
      activeStyle: css({
        ...baseStyle,
        padding,
        border: '1px solid #0073BF',
        backgroundColor: '#F9FDFF',
      }),
    }
  }, [css, compact])

  return (
    <div
      role="button"
      tabIndex={0}
      className={isSelected ? activeStyle : normalStyle}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
    >
      {icon && <div className="mb-xxs">{icon}</div>}
      <Text.BodyText4 tag="div" bold>
        {title}
      </Text.BodyText4>
      <Text.BodyText4
        tag="div"
        color="grey50"
        className={compact ? '' : 'mt-xxs'}
      >
        {description}
      </Text.BodyText4>
    </div>
  )
}

export default React.memo(SelectableCard)
